// External dependencies
import React, { useEffect, useState } from 'react';

// Internal dependencies
import AdminLayout from '@/modules/admin/client/components/AdminLayout.component';
import CmsAdminNav from '@/modules/cms/client/components/CmsAdminNav.component';
import {
  getTrash,
  purgePage,
  restorePage,
} from '@/modules/cms/client/api/admin-cms.api';

export default function AdminCmsTrash() {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = () => {
    setIsLoading(true);
    setError(null);

    getTrash()
      .then(setPages)
      .catch(() => setError('Failed to load trash.'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleRestore = async page => {
    setError(null);
    setSuccess(null);

    try {
      await restorePage(page._id);
      setPages(previous => previous.filter(item => item._id !== page._id));
      setSuccess(`“${page.title}” restored.`);
    } catch (err) {
      // A slug conflict comes back as 409 with an actionable message; show it
      // rather than a generic failure.
      const message =
        err.response && err.response.data && err.response.data.message;
      setError(message || 'Failed to restore page.');
    }
  };

  const handlePurge = async page => {
    // Double confirm: this is the only unrecoverable action in the module.
    if (
      !window.confirm(
        `Permanently delete “${page.title}”? This cannot be undone.`,
      )
    ) {
      return;
    }

    if (
      !window.confirm(
        'Really delete permanently? The page and its version history will be destroyed.',
      )
    ) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      await purgePage(page._id);
      setPages(previous => previous.filter(item => item._id !== page._id));
      setSuccess(`“${page.title}” permanently deleted.`);
    } catch (err) {
      setError('Failed to delete page.');
    }
  };

  return (
    <AdminLayout>
      <CmsAdminNav />
      <div className="container">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-12">
            <h2>Trash</h2>
            <p>
              Deleted pages and blog posts are kept here. They are hidden from
              the site and from their lists until restored.
            </p>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {isLoading ? (
          <p>Loading trash…</p>
        ) : (
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Slug</th>
                <th>Language</th>
                <th>Deleted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map(page => (
                <tr key={page._id}>
                  <td>{page.title}</td>
                  <td>{page.kind === 'post' ? 'Post' : 'Page'}</td>
                  <td>
                    <code>{page.slug}</code>
                  </td>
                  <td>{page.locale}</td>
                  <td>
                    {page.deletedAt
                      ? new Date(page.deletedAt).toLocaleString()
                      : '—'}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-primary btn-xs"
                      style={{ marginRight: '5px' }}
                      onClick={() => handleRestore(page)}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-xs"
                      onClick={() => handlePurge(page)}
                    >
                      Delete permanently
                    </button>
                  </td>
                </tr>
              ))}
              {!pages.length && (
                <tr>
                  <td colSpan="6">Trash is empty.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}

AdminCmsTrash.propTypes = {};
